import { C } from '../data/constants.js';

export class ScoreManager {
  constructor() {
    this.score = 0;
    this.highScore = this._loadHighScore();
    this.combo = 0;
    this.comboTimer = 0;  // ms since last brick hit
    this.lives = C.STARTING_LIVES;
    
    // Score popups (floating text)
    this.popups = [];

    // Combo callouts ("FRENZY!", "UNSTOPPABLE!", etc.)
    this.callouts = [];
  }

  _loadHighScore() {
    try {
      const saved = localStorage.getItem('bricksYoHighScore') || localStorage.getItem('shatterYoHighScore');
      return parseInt(saved || '0', 10);
    } catch {
      return 0;
    }
  }

  _saveHighScore() {
    try {
      localStorage.setItem('bricksYoHighScore', String(this.highScore));
    } catch {
      // localStorage unavailable
    }
  }

  addBrickScore(points, brickCenterX, brickCenterY) {
    const multiplier = 1 + this.combo * C.COMBO_MULTIPLIER_STEP;
    const finalPoints = Math.round(points * multiplier);
    this.score += finalPoints;
    
    if (this.score > this.highScore) {
      this.highScore = this.score;
      this._saveHighScore();
    }
    
    this.combo++;
    this.comboTimer = 0;
    
    // Create score popup
    this.popups.push({
      text: `+${finalPoints}`,
      x: brickCenterX,
      y: brickCenterY,
      life: 55,
      maxLife: 55
    });

    // Trigger milestone combo callouts
    if (this.combo === 5) {
      this.triggerCallout('⚡ FRENZY! ⚡', '#00ffff');
    } else if (this.combo === 10) {
      this.triggerCallout('🔥 UNSTOPPABLE! 🔥', '#ffaa00');
    } else if (this.combo === 20) {
      this.triggerCallout('💥 SHATTER STORM! 💥', '#ff00ff');
    }
  }

  triggerCallout(text, color = '#00ffff') {
    this.callouts.push({
      text,
      color,
      life: 60,
      maxLife: 60,
      y: C.CANVAS_H / 2 - 30,
    });
  }

  loseLife() {
    this.lives--;
    this.combo = 0;
    this.comboTimer = 0;
    return this.lives;
  }

  update(dtMs) {
    this.comboTimer += dtMs;
    if (this.comboTimer > C.COMBO_TIMEOUT) {
      this.combo = 0;
    }
    
    // Update score popups
    for (let i = this.popups.length - 1; i >= 0; i--) {
      const p = this.popups[i];
      p.y -= 0.6;
      p.life--;
      if (p.life <= 0) {
        this.popups.splice(i, 1);
      }
    }

    // Update callouts
    for (let i = this.callouts.length - 1; i >= 0; i--) {
      const c = this.callouts[i];
      c.y -= 0.4;
      c.life--;
      if (c.life <= 0) {
        this.callouts.splice(i, 1);
      }
    }
  }

  renderPopups(ctx) {
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = 'bold 12px monospace';
    
    for (const p of this.popups) {
      const alpha = p.life / p.maxLife;
      const scale = 1 + (1 - alpha) * 0.4;
      ctx.globalAlpha = alpha;
      ctx.fillStyle = '#ffff00';
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.scale(scale, scale);
      ctx.fillText(p.text, 0, 0);
      ctx.restore();
    }
    ctx.globalAlpha = 1;

    // Render Combo Callouts
    for (const c of this.callouts) {
      const alpha = c.life / c.maxLife;
      const scale = 1.2 - (c.life / c.maxLife) * 0.2;
      ctx.globalAlpha = alpha;
      ctx.font = 'bold 22px monospace';
      ctx.fillStyle = c.color;
      ctx.shadowColor = c.color;
      ctx.shadowBlur = 15;
      ctx.save();
      ctx.translate(C.CANVAS_W / 2, c.y);
      ctx.scale(scale, scale);
      ctx.fillText(c.text, 0, 0);
      ctx.restore();
      ctx.shadowBlur = 0;
    }
    ctx.globalAlpha = 1;
  }

  renderHUD(ctx) {
    ctx.fillStyle = 'rgba(0, 0, 0, 0.65)';
    ctx.fillRect(0, 0, C.CANVAS_W, C.HUD_HEIGHT);
    
    // Lives (hearts)
    ctx.font = C.HUD_FONT;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#ff4466';
    let livesText = '';
    for (let i = 0; i < this.lives; i++) livesText += '♥ ';
    ctx.fillText(livesText, 10, C.HUD_HEIGHT / 2);
    
    // Score (center)
    ctx.textAlign = 'center';
    ctx.fillStyle = C.HUD_COLOR;
    ctx.font = 'bold 14px monospace';
    ctx.fillText(`${this.score.toLocaleString()}`, C.CANVAS_W / 2, C.HUD_HEIGHT / 2);
    
    // High score (right)
    ctx.textAlign = 'right';
    ctx.fillStyle = '#888888';
    ctx.font = '11px monospace';
    ctx.fillText(`HI ${this.highScore.toLocaleString()}`, C.CANVAS_W - 10, C.HUD_HEIGHT / 2);
    
    // Combo meter bar
    if (this.combo > 0) {
      const comboW = Math.min(this.combo * 22, C.CANVAS_W - 20);
      ctx.fillStyle = '#ffaa00';
      ctx.shadowColor = '#ffaa00';
      ctx.shadowBlur = 6;
      ctx.fillRect(10, C.HUD_HEIGHT + 2, comboW, 3);
      ctx.shadowBlur = 0;
      
      ctx.font = '10px monospace';
      ctx.textAlign = 'left';
      ctx.fillStyle = '#ffaa00';
      ctx.fillText(`×${(1 + this.combo * C.COMBO_MULTIPLIER_STEP).toFixed(1)}`, comboW + 14, C.HUD_HEIGHT + 4);
    }
  }

  reset() {
    this.score = 0;
    this.combo = 0;
    this.comboTimer = 0;
    this.lives = C.STARTING_LIVES;
    this.popups = [];
    this.callouts = [];
  }
}
